import { useTranslation } from '../../../i18n';
import { FormField } from '../../../components/Shared/FormField';
import { formFieldInputClassName } from '../../../components/Shared/formFieldStyles';
import { PAYMENT_INTERVALS, paymentIntervalLabelKey, toPaymentInterval } from '../../../types/billing';
import { validateStandingPlan, type StandingPlan } from '@/utils/standingPlan';

interface StandingInvoicePlanFieldsProps {
  value: StandingPlan;
  onChange: (plan: StandingPlan) => void;
  disabled?: boolean;
  /** Prefix of the input ids — two instances may be on one page. */
  idPrefix?: string;
}

/**
 * Plan of a standing invoice (FAFD-27): service period in whole months plus payment interval.
 * Shows the client-side checks mirroring `invoices.standingPeriodInvalid`; the backend still
 * validates on preview and create.
 */
export function StandingInvoicePlanFields({
  value,
  onChange,
  disabled,
  idPrefix = 'standing-plan',
}: StandingInvoicePlanFieldsProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const errors = validateStandingPlan(value);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <FormField
        id={`${idPrefix}-from`}
        label={t('invoices:standingPlan.from')}
        type="date"
        required
        disabled={disabled}
        value={value.from}
        onChange={(e) => onChange({ ...value, from: e.target.value })}
        error={errors.from ? t(`invoices:${errors.from}`) : undefined}
      />
      <FormField
        id={`${idPrefix}-to`}
        label={t('invoices:standingPlan.to')}
        type="date"
        required
        disabled={disabled}
        value={value.to}
        onChange={(e) => onChange({ ...value, to: e.target.value })}
        error={errors.to ? t(`invoices:${errors.to}`) : undefined}
      />
      <FormField label={t('invoices:standingPlan.interval')} required>
        <select
          id={`${idPrefix}-interval`}
          value={value.interval}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, interval: toPaymentInterval(e.target.value) })}
          aria-label={t('invoices:standingPlan.interval')}
          className={formFieldInputClassName()}
        >
          {PAYMENT_INTERVALS.map((interval) => (
            <option key={interval} value={interval}>
              {t(paymentIntervalLabelKey(interval))}
            </option>
          ))}
        </select>
      </FormField>
    </div>
  );
}
